/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import ApiService from "../../../core/service/ApiService"
import { loc, userConfigurationService } from "../../../core/utils"
import { normalizeVoyageurLangCode } from "../../../core/voyageurLanguage"
import { mademoiselleEnumCode } from "../constants/ClientPageConstants"

/** Codes titre civil API (alignés sur tunnelData.data.civilStatus) → clés i18n stables */
const CIVILITY_TITLE_I18N_KEYS = {
  "1": "civility_mr",
  "2": "civility_mrs"
}

export function getUserCountryCode() {
  let config = userConfigurationService.getUserConfiguration()
  return config.user.locale.split("-")[1]
}

function getLanguageTag(tunnelData) {
  return (
    normalizeVoyageurLangCode(tunnelData?.data?.order?.orderLanguageTag) ||
    normalizeVoyageurLangCode(userConfigurationService.getUserConfiguration()?.user?.locale)
  )
}

export function getHousingTypes(tunnelData) {
  let housingTypes = tunnelData.housingTypes || []
  let result = []
  for (const [key, value] of Object.entries(housingTypes)) {
    result.push({ value: key, label: value })
  }
  result.sort(function (reference, otherReference) {
    if (reference.label < otherReference.label) {
      return -1
    }
    if (reference.label > otherReference.label) {
      return 1
    }
    return 0
  })
  return result
}

export function getFamilySituations(tunnelData) {
  let familySituations = tunnelData.familySituations || []
  let result = []
  for (const [key, value] of Object.entries(familySituations)) {
    result.push({ value: key, label: value })
  }
  result.sort(function (reference, otherReference) {
    if (reference.label < otherReference.label) {
      return -1
    }
    if (reference.label > otherReference.label) {
      return 1
    }
    return 0
  })
  return result
}

export function isASocioProfessionalCategoryWithoutContract(
  socioProfessionalRepositoryList,
  socioProfessionalCategoryCode
) {
  if (socioProfessionalCategoryCode) {
    if (socioProfessionalCategoryCode && socioProfessionalRepositoryList) {
      for (const [key] of Object.entries(socioProfessionalRepositoryList)) {
        if (socioProfessionalCategoryCode === key) {
          return true
        }
      }
    }
  }
  return false
}

export async function getCountriesCode(tunnelData) {
  const languageTag = getLanguageTag(tunnelData)
  const getCountriesCodeURL =
    tunnelData?.apiUrl +
    "middle-business-service/api/v2/repository/countries/codes/" +
    getUserCountryCode() +
    (languageTag ? `?languageTag=${encodeURIComponent(languageTag)}` : "")
  const headers = {
    Authorization: `Bearer ${tunnelData?.access_token}`,
    Accept: "*/*"
  }
  return await ApiService.getForEvollisApi(getCountriesCodeURL, headers)
}

export async function getDepatments(tunnelData, countryCode) {
  const getDepartmentsURL =
    tunnelData?.apiUrl + "middle-business-service/api/v2/repository/departments/codes?countryCode=" + countryCode
  const headers = {
    Authorization: `Bearer ${tunnelData?.access_token}`,
    Accept: "*/*"
  }
  return await ApiService.getForEvollisApi(getDepartmentsURL, headers)
}

export async function getCities(tunnelData, departmentCode) {
  const languageTag = getLanguageTag(tunnelData)
  const getDepartmentsURL =
    tunnelData?.apiUrl +
    "middle-business-service/api/v2/repository/cities/?departmentId=" +
    departmentCode +
    (languageTag ? `&languageTag=${encodeURIComponent(languageTag)}` : "")
  const headers = {
    Authorization: `Bearer ${tunnelData?.access_token}`,
    Accept: "*/*"
  }
  return await ApiService.getForEvollisApi(getDepartmentsURL, headers)
}

export function getCivilStatuses(tunnelData) {
  let result = []
  for (const [key, value] of Object.entries(tunnelData?.data?.civilStatus || {})) {
    if (key !== mademoiselleEnumCode) {
      const i18nKey = CIVILITY_TITLE_I18N_KEYS[key]
      const label =
        i18nKey != null
          ? loc(i18nKey)
          : typeof value === "string"
            ? loc(value.trim())
            : value
      result.push({ name: key, label })
    }
  }
  return result
}

export function getTemporaryDocuments(tunnelData) {
  let result = []
  for (const [key, value] of Object.entries(tunnelData?.data?.temporaryDocuments)) {
    result.push({ name: key, label: value })
  }
  return result
}
export function getTemporaryDocumentsLabel(tunnelData, id) {
  let result = getTemporaryDocuments(tunnelData)
  return result.filter((item) => item.name === id)[0]?.label
}

export function getCivilStatusLabel(tunnelData, id) {
  let result = getCivilStatuses(tunnelData)
  return result.filter((item) => item.name === id)[0]?.label
}

export function getListFromRepository(repository) {
  let result = []
  for (const [key, value] of Object.entries(repository)) {
    result.push({ value: key, label: value })
  }
  return result
}

export function getListFromRepositorySorted(repository) {
  let result = []
  for (const [key, value] of Object.entries(repository)) {
    result.push({ value: key, label: value })
  }
  return result
}

export function getLabelById(repository, id) {
  if (!id) return []
  let result = getListFromRepository(repository)
  return result.filter((item) => item.value === id)[0]?.label
}
